import React, { useState, useEffect } from 'react';
import { Zap, Mail, CheckCircle, XCircle, Clock, AlertTriangle, TrendingUp, Send, RefreshCw, Play } from 'lucide-react';
import { realtimeExecutionService } from '../../../services/realtimeExecutionService';
import { emailProcessingService } from '../../../services/emailProcessingService';
import LiveIndicator from '../../../components/ui/LiveIndicator';
import Icon from '../../../components/AppIcon';


const ExecutionPanel = () => {
  const [activeExecutions, setActiveExecutions] = useState([]);
  const [queuedEmails, setQueuedEmails] = useState([]);
  const [deliveryStatus, setDeliveryStatus] = useState([]);
  const [failureLogs, setFailureLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [retrying, setRetrying] = useState({});
  const [activeTab, setActiveTab] = useState('active'); // active, queued, delivery, failures
  const [lastUpdate, setLastUpdate] = useState(new Date());

  useEffect(() => {
    loadAllData();

    // Setup real-time subscriptions
    const executionsChannel = realtimeExecutionService?.subscribeToExecutions((payload) => {
      console.log('Execution update:', payload);
      loadActiveExecutions();
      setLastUpdate(new Date());
    });

    const recipientsChannel = realtimeExecutionService?.subscribeToRecipients((payload) => {
      console.log('Recipients update:', payload);
      loadQueuedEmails();
      setLastUpdate(new Date());
    });

    const emailLogsChannel = realtimeExecutionService?.subscribeToEmailLogs((payload) => {
      console.log('Email logs update:', payload);
      loadDeliveryStatus();
      loadFailureLogs();
      setLastUpdate(new Date());
    });

    // Cleanup subscriptions
    return () => {
      realtimeExecutionService?.unsubscribe(executionsChannel);
      realtimeExecutionService?.unsubscribe(recipientsChannel);
      realtimeExecutionService?.unsubscribe(emailLogsChannel);
    };
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    await Promise.all([
      loadActiveExecutions(),
      loadQueuedEmails(),
      loadDeliveryStatus(),
      loadFailureLogs()
    ]);
    setLoading(false);
  };

  const loadActiveExecutions = async () => {
    const data = await realtimeExecutionService?.getActiveExecutions();
    setActiveExecutions(data);
  };

  const loadQueuedEmails = async () => {
    const data = await realtimeExecutionService?.getQueuedEmails();
    setQueuedEmails(data);
  };

  const loadDeliveryStatus = async () => {
    const data = await realtimeExecutionService?.getDeliveryStatus(50);
    setDeliveryStatus(data);
  };

  const loadFailureLogs = async () => {
    const data = await realtimeExecutionService?.getFailureLogs(30);
    setFailureLogs(data);
  };

  const handleProcessEmails = async () => {
    setProcessing(true);
    try {
      const result = await emailProcessingService?.processQueuedEmails();
      if (result?.success) {
        // Refresh data after processing
        await loadAllData();
        alert(`Emails procesados: ${result?.data?.results?.sent || 0} enviados, ${result?.data?.results?.failed || 0} fallidos`);
      } else {
        alert(`Error al procesar emails: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error processing emails:', error);
      alert('Error al procesar emails');
    } finally {
      setProcessing(false);
    }
  };

  const handleRetryEmail = async (recipientId) => {
    setRetrying(prev => ({ ...prev, [recipientId]: true }));
    try {
      const result = await emailProcessingService?.retryFailedEmail(recipientId);
      if (result?.success) {
        await loadAllData();
        alert('Email reintentado exitosamente');
      } else {
        alert(`Error al reintentar: ${result?.error}`);
      }
    } catch (error) {
      console.error('Error retrying email:', error);
      alert('Error al reintentar email');
    } finally {
      setRetrying(prev => ({ ...prev, [recipientId]: false }));
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date?.toLocaleString('es-MX', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pendiente' },
      running: { color: 'bg-blue-100 text-blue-800', icon: TrendingUp, label: 'Ejecutando' },
      sent: { color: 'bg-green-100 text-green-800', icon: Send, label: 'Enviado' },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Entregado' },
      failed: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Fallido' },
      bounced: { color: 'bg-orange-100 text-orange-800', icon: AlertTriangle, label: 'Rebotado' }
    };

    const config = statusConfig?.[status?.toLowerCase()] || statusConfig?.pending;
    const Icon = config?.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config?.color}`}>
        <Icon className="w-3 h-3" />
        {config?.label}
      </span>
    );
  };

  const tabs = [
    { id: 'active', label: 'Activos', count: activeExecutions?.length, icon: Zap },
    { id: 'queued', label: 'En Cola', count: queuedEmails?.length, icon: Clock },
    { id: 'delivery', label: 'Estado de Entrega', count: deliveryStatus?.length, icon: CheckCircle },
    { id: 'failures', label: 'Fallos', count: failureLogs?.length, icon: XCircle }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Zap className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-foreground">Panel de Ejecución en Tiempo Real</h3>
              <p className="text-sm text-muted-foreground">Monitoreo de triggers activos y estado de emails</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleProcessEmails}
              disabled={processing || queuedEmails?.length === 0}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {processing ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Play className="w-4 h-4" />
              )}
              {processing ? 'Procesando...' : 'Procesar Cola'}
            </button>
            <LiveIndicator isLive={true} lastUpdate={lastUpdate} />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex overflow-x-auto">
          {tabs?.map((tab) => {
            const Icon = tab?.icon;
            return (
              <button
                key={tab?.id}
                onClick={() => setActiveTab(tab?.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab?.id
                    ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-gray-50'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{tab?.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                  activeTab === tab?.id ? 'bg-primary text-white' : 'bg-gray-200 text-gray-700'
                }`}>
                  {tab?.count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <>
            {/* Active Executions Tab */}
            {activeTab === 'active' && (
              <div className="space-y-3">
                {activeExecutions?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Zap className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay ejecuciones activas en este momento</p>
                  </div>
                ) : (
                  activeExecutions?.map((execution) => (
                    <div key={execution?.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{execution?.rule?.ruleName}</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Tipo: <span className="font-medium">{execution?.rule?.triggerType}</span>
                          </p>
                        </div>
                        {getStatusBadge(execution?.executionStatus)}
                      </div>
                      <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Clientes Coincidentes</p>
                          <p className="font-semibold text-foreground">{execution?.matchedClientsCount || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Emails Enviados</p>
                          <p className="font-semibold text-foreground">{execution?.emailsSentCount || 0}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Iniciado</p>
                          <p className="font-semibold text-foreground">{formatTimestamp(execution?.triggeredAt)}</p>
                        </div>
                      </div>
                      {execution?.errorMessage && (
                        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-800">
                          <strong>Error:</strong> {execution?.errorMessage}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Queued Emails Tab */}
            {activeTab === 'queued' && (
              <div className="space-y-3">
                {queuedEmails?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay emails en cola</p>
                  </div>
                ) : (
                  <>
                    <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-blue-900">
                          {queuedEmails?.length} emails pendientes de envío
                        </span>
                      </div>
                    </div>
                    {queuedEmails?.map((email) => (
                      <div key={email?.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-foreground">{email?.recipientName || 'Sin nombre'}</p>
                            <p className="text-sm text-muted-foreground">{email?.recipientEmail}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-xs text-muted-foreground">Regla</p>
                            <p className="text-sm font-medium text-foreground">
                              {email?.execution?.rule?.ruleName || 'N/A'}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}

            {/* Delivery Status Tab */}
            {activeTab === 'delivery' && (
              <div className="space-y-3">
                {deliveryStatus?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>No hay registros de entrega disponibles</p>
                  </div>
                ) : (
                  deliveryStatus?.map((log) => (
                    <div key={log?.id} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1">
                          <h4 className="font-semibold text-foreground">{log?.subject}</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Para: <span className="font-medium">{log?.recipientEmail}</span>
                            {log?.client?.name && (
                              <span className="ml-2">({log?.client?.name})</span>
                            )}
                          </p>
                        </div>
                        {getStatusBadge(log?.status)}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3 text-sm">
                        <div>
                          <p className="text-muted-foreground">Enviado</p>
                          <p className="font-semibold text-foreground">{formatTimestamp(log?.sentAt)}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Aperturas</p>
                          <p className="font-semibold text-foreground">
                            {log?.openCount || 0}
                            {log?.openedAt && (
                              <span className="text-xs text-muted-foreground ml-1">({formatTimestamp(log?.openedAt)})</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Clics</p>
                          <p className="font-semibold text-foreground">
                            {log?.clickCount || 0}
                            {log?.clickedAt && (
                              <span className="text-xs text-muted-foreground ml-1">({formatTimestamp(log?.clickedAt)})</span>
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-muted-foreground">Engagement</p>
                          <p className="font-semibold text-foreground">
                            {log?.openedAt && log?.clickedAt ? (
                              <span className="text-green-600">Alto</span>
                            ) : log?.openedAt ? (
                              <span className="text-yellow-600">Medio</span>
                            ) : (
                              <span className="text-gray-600">Bajo</span>
                            )}
                          </p>
                        </div>
                      </div>
                      {log?.errorMessage && (
                        <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          <span className="font-medium">Error:</span> {log?.errorMessage}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            )}

            {/* Failure Logs Tab */}
            {activeTab === 'failures' && (
              <div className="space-y-3">
                {failureLogs?.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle className="w-12 h-12 mx-auto mb-2 opacity-50 text-green-500" />
                    <p className="text-green-600 font-medium">¡Sin fallos! Todo funciona correctamente</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {failureLogs?.map((log) => (
                      <div key={log?.id} className="p-4 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="w-3 h-3" />
                                {log?.type === 'execution' ? 'Ejecución' : log?.type === 'recipient' ? 'Destinatario' : 'Email'}
                              </span>
                              <span className="text-xs text-muted-foreground">{formatTimestamp(log?.timestamp)}</span>
                            </div>
                            {log?.ruleName && (
                              <p className="text-sm font-medium text-foreground mb-1">Regla: {log?.ruleName}</p>
                            )}
                            {log?.recipientEmail && (
                              <p className="text-sm text-muted-foreground mb-1">Para: {log?.recipientEmail}</p>
                            )}
                            {log?.subject && (
                              <p className="text-sm text-muted-foreground mb-1">Asunto: {log?.subject}</p>
                            )}
                            <p className="text-sm text-red-600 mt-2">{log?.errorMessage}</p>
                          </div>
                          {log?.type === 'recipient' && (
                            <button
                              onClick={() => handleRetryEmail(log?.id)}
                              disabled={retrying?.[log?.id]}
                              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 rounded-lg hover:bg-primary/20 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                              {retrying?.[log?.id] ? (
                                <RefreshCw className="w-3 h-3 animate-spin" />
                              ) : (
                                <RefreshCw className="w-3 h-3" />
                              )}
                              Reintentar
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ExecutionPanel;